import type { Dispatch, SetStateAction } from "react"
import { useCallback, useContext, useLayoutEffect, useState } from "react"

import { Box, ButtonBase, Link, Stack, useMediaQuery } from "@mui/material"
import { alpha, styled, useTheme } from "@mui/material/styles"
import { get } from "lodash"

import { useScrollDirection } from "@ggs/hooks"
import type { FC, FCWC, ResponsiveImageType, SC, TripData } from ".."
import {
  Button,
  IconButton,
  InquiryModal,
  Logo,
  AutocompleteSearch,
  SvgIcon,
  TripModal,
  UserCountrySelect,
  NavigationFavorites,
} from ".."
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import { HeaderNav, type HeaderNavLink } from "./HeaderNav"
import { HeaderNavMobile } from "./HeaderNavMobile"

type HeaderSliceProps = {
  headerMenuLinksTree?: HeaderNavLink[]
  hideOnScroll?: boolean
}

const Header: SC<{}, HeaderSliceProps> = (props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const menuLinks = get(props, "sliceContext.headerMenuLinksTree", [] as HeaderNavLink[])
  const trip = get(props, "trip", null)
  const showContactInfo = get(props, "showContactInfo", true)
  const hideOnScroll = get(props, "hideOnScroll", false)

  return (
    <HeaderContainer hideOnScroll={hideOnScroll}>
      <HeaderContent
        setOpenMobileMenu={setOpenMobileMenu}
        trip={trip}
        showContactInfo={showContactInfo}
      />
      {isMobile ? (
        <HeaderNavMobile
          open={openMobileMenu}
          setOpen={setOpenMobileMenu}
          links={menuLinks as HeaderNavLink[]}
          showContactInfo={showContactInfo}
        />
      ) : (
        <HeaderNav links={menuLinks as HeaderNavLink[]} />
      )}
    </HeaderContainer>
  )
}

export default Header

/** A sub-component for the container of the header. */
type HeaderContainerProps = {
  hideOnScroll?: boolean
}

const HeaderContainer: FCWC<HeaderContainerProps> = ({ children, hideOnScroll }) => {
  const direction = useScrollDirection(150)
  const [atTop, setAtTop] = useState(true)
  const [isHidden, setIsHidden] = useState(false)

  const checkTop = useCallback(() => {
    const isAtTop = window.scrollY < 10
    setAtTop(isAtTop)
    if (isAtTop) {
      setIsHidden(false)
    }
  }, [])

  useLayoutEffect(() => {
    checkTop()
    window.addEventListener("scroll", checkTop)
    return () => window.removeEventListener("scroll", checkTop)
  }, [])

  useLayoutEffect(() => {
    if (hideOnScroll && direction === "DOWN" && !atTop) {
      setIsHidden(true)
    }
  }, [direction, hideOnScroll, atTop])

  return (
    <Box
      data-name="header-container"
      component="section"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex={3000}
      p={2.5}
      pb={0}
      sx={{
        transform: hideOnScroll
          ? isHidden
            ? "translateY(-100%)"
            : "translateY(0)"
          : direction === "DOWN"
          ? "translateY(-100%)"
          : "translateY(0)",
        transition: (theme) => theme.transitions.create("transform"),
      }}
    >
      <Box position="relative" mx="auto" width="100%">
        <Box
          bgcolor={(theme) => theme.palette["tertiary.200"].main}
          borderRadius={1.5}
          sx={(theme) => ({
            boxShadow:
              direction === "DOWN" || atTop
                ? "none"
                : `0px 0px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.2)}`,
            transition: theme.transitions.create("box-shadow"),
          })}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

/** A sub-component for the top nav section of the header. */
type HeaderContentProps = {
  setOpenMobileMenu: Dispatch<SetStateAction<boolean>>
  trip?: {
    image: ResponsiveImageType
    title: string
    slug: string
    primaryCountry: string
    destinationSlug: string
    id: string
  } | null
  showContactInfo: boolean
}

const HeaderContent: FC<HeaderContentProps> = ({ setOpenMobileMenu, trip, showContactInfo }) => {
  const [openInquiryModal, setOpenInquiryModal] = useState(false)
  const [openSearchModal, setOpenSearchModal] = useState(false)
  const [openTripModal, setOpenTripModal] = useState(false)

  const siteMetadata = useContext(SiteMetadataContext)
  const isBrowser = typeof window !== "undefined"
  const isSearchPage = isBrowser ? window.location.pathname === "/search" : false
  return (
    <>
      <Stack
        component="section"
        data-name="header-top-nav"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        p={{ md: 2.5 }}
        pr={{ md: 2.5 }}
        pl={{ xs: 3.5, md: 7 }}
        maxHeight={{ xs: 54, md: 72 }}
      >
        <Logo size={{ height: { xs: 22, md: 36 }, width: { xs: 84, md: 137 } }} />

        <Stack direction="row" alignItems="center" gap={3} display={{ md: "none" }}>
          {!isSearchPage && (
            <SearchButton onClick={() => setOpenSearchModal(true)}>
              <SvgIcon variant="search" strokeMode size={18} sx={{ strokeWidth: 2 }} />
            </SearchButton>
          )}
          {showContactInfo && (
            <Button
              variant="quaternary"
              aria-label="Inquire Now"
              onClick={() => {
                if (trip) {
                  setOpenTripModal(true)
                } else {
                  setOpenInquiryModal(true)
                }
              }}
              sx={{
                minHeight: "40px!important",
                alignSelf: "center",
                padding: "8px!important",
                display: { xs: "inline-flex", md: "none" },
              }}
            >
              Inquire Now
            </Button>
          )}
          <ButtonBase
            onClick={() => setOpenMobileMenu(true)}
            sx={{
              flexDirection: "column",
              rowGap: 1.5,
              width: 54,
              height: 54,
              borderLeft: (theme) => `1px solid ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
              display: { xs: "inline-flex", md: "none" },
            }}
          >
            <Box
              role="none"
              width={26}
              height="1px"
              bgcolor={(theme) => theme.palette["neutral.700"].main}
              borderRadius={9999}
            />
            <Box
              role="none"
              width={26}
              height="1px"
              bgcolor={(theme) => theme.palette["neutral.700"].main}
              borderRadius={9999}
            />
            <Box
              role="none"
              width={26}
              height="1px"
              bgcolor={(theme) => theme.palette["neutral.700"].main}
              borderRadius={9999}
            />
          </ButtonBase>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={8}
          display={{ xs: "none", md: "flex" }}
        >
          <UserCountrySelect />

          <Box
            sx={(theme) => ({
              "display": "flex",
              "padding": 2.5,
              "mr": -2.5,
              "borderRadius": 9999,
              "transition": theme.transitions.create("background-color"),
              "&:hover": {
                background: theme.palette["tertiary.300"].main,
              },
            })}
          >
            <NavigationFavorites size={18} />
          </Box>
          {showContactInfo && (
            <>
              <Link
                href={siteMetadata.phoneUrl}
                variant="link.sm"
                color={(theme) => theme.palette["neutral.700"].main}
                underline="none"
              >
                {siteMetadata.phone}
              </Link>
            </>
          )}
          <Box>
            {!isSearchPage && (
              <SearchButton onClick={() => setOpenSearchModal(true)}>
                <SvgIcon variant="search" strokeMode size={18} sx={{ strokeWidth: 2 }} />
                <Box component="span">Search</Box>
              </SearchButton>
            )}
          </Box>
          {showContactInfo && (
            <Button
              variant="quaternary"
              aria-label="Inquire Now"
              sx={(theme) => ({
                background: alpha(theme.palette["primary.800"].main, 0.08),
                color: theme.palette["neutral.800"].main,
                fontSize: 15,
                fontWeight: 500,
                minHeight: "auto !important",
                height: { xs: 40, md: 50 },
                py: { xs: 3.5, md: 3 },
                px: { xs: 3, md: 6 },
              })}
              onClick={() => {
                if (trip) {
                  setOpenTripModal(true)
                } else {
                  setOpenInquiryModal(true)
                }
              }}
            >
              Inquire Now
            </Button>
          )}
        </Stack>
      </Stack>
      {openSearchModal && <AutocompleteSearch onClose={() => setOpenSearchModal(false)} />}
      <InquiryModal open={openInquiryModal} onClose={() => setOpenInquiryModal(false)} />
      {trip && (
        <TripModal
          open={openTripModal}
          onClose={() => setOpenTripModal(false)}
          trip={trip as TripData}
        />
      )}
    </>
  )
}

const SearchButton = styled(ButtonBase)(({ theme }) => ({
  ...theme.typography["body.md"],
  "alignItems": "center",
  "backgroundColor": "transparent",
  "border": `1px solid transparent`,
  "borderRadius": theme.spacing(1.5),
  "display": "flex",
  "gap": theme.spacing(2),
  "padding": theme.spacing(1),
  "margin": 0,
  "height": 34,
  "width": 34,
  "transition": theme.transitions.create(["background-color", "border-color", "color"]),
  "&:hover": {
    background: theme.palette["tertiary.300"].main,
  },

  [theme.breakpoints.up("md")]: {
    border: `1px solid ${theme.palette["neutral.200"].main}`,
    padding: theme.spacing(3, 4),
    height: 50,
    width: "auto",
  },
}))
